import type { HfaItem, HfaDimMeta } from '../types'

// ─── All 17 HFA items ─────────────────────────────────────────────────────────
// No reverse-coded items. BO3 uses a frequency scale (noted in noteText).
// Source: Clutch_HFA_Habit_Adoption_Assessment_v3

export const HFA_ITEMS: HfaItem[] = [
  // Enablement & Environment (EE) — 4 items
  { id: 'EE1', dim: 'ee', text: 'I have the time and capacity in my normal workday to perform this behavior consistently.' },
  { id: 'EE2', dim: 'ee', text: 'The tools, systems, and resources I need to perform this behavior are available and work as expected.' },
  { id: 'EE3', dim: 'ee', text: 'My workflow/process makes it easy to perform this behavior (it fits naturally into how work gets done).' },
  { id: 'EE4', dim: 'ee', text: 'If I get stuck, I know where to go for quick help (job aids, peer support, manager support, training).' },

  // Planning & Routine Design (PR) — 3 items
  { id: 'PR1', dim: 'pr', text: 'I have a clear plan for when and where I will perform this behavior.' },
  { id: 'PR2', dim: 'pr', text: 'I have built this behavior into an existing routine (e.g., "after I do X, I do this").' },
  { id: 'PR3', dim: 'pr', text: 'I have reduced friction so the behavior is easy to start (e.g., templates ready, steps simplified, materials prepared).' },

  // Cues & Triggers (CT) — 3 items
  { id: 'CT1', dim: 'ct', text: 'I have a reliable reminder or cue that prompts me to perform this behavior.' },
  { id: 'CT2', dim: 'ct', text: 'When the cue occurs, I notice it and start the behavior quickly.' },
  { id: 'CT3', dim: 'ct', text: 'If a cue isn’t working, I try different reminders/prompts until I find one that does.' },

  // Reinforcement & Feedback (RF) — 3 items
  { id: 'RF1', dim: 'rf', text: 'I feel a sense of progress or satisfaction immediately after performing the behavior.' },
  { id: 'RF2', dim: 'rf', text: 'I have a way to track and/or celebrate progress when I perform the behavior consistently.' },
  { id: 'RF3', dim: 'rf', text: 'I receive timely feedback or recognition from others (peers/manager/system feedback) when I perform the behavior.' },

  // Behavioral Outcomes (BO) — 4 items
  { id: 'BO1', dim: 'bo', text: 'I perform this behavior automatically (it feels increasingly "second nature").' },
  { id: 'BO2', dim: 'bo', text: 'If I miss the behavior once, I return to it at the next appropriate opportunity.' },
  {
    id: 'BO3',
    dim: 'bo',
    text: 'In the past 10 workdays, I performed this behavior:',
    noteText: '1 = 0–1 times · 2 = 2–3 · 3 = 4–5 · 4 = 6–8 · 5 = 9–10 times',
  },
  { id: 'BO4', dim: 'bo', text: 'When I perform this behavior, I meet the expected quality/standard without needing significant rework.' },
]

// ─── Dimension metadata ────────────────────────────────────────────────────────

export const HFA_DIMS: HfaDimMeta[] = [
  {
    key: 'ee',
    label: 'Enablement & Environment',
    subtitle: 'Time, tools, workflow fit, access to help',
    icon: '🔧',
    indicatorType: 'leading',
  },
  {
    key: 'pr',
    label: 'Planning & Routine Design',
    subtitle: 'Whether the behavior is scaffolded into routines',
    icon: '📅',
    indicatorType: 'leading',
  },
  {
    key: 'ct',
    label: 'Cues & Triggers',
    subtitle: 'Whether reliable prompts exist and are noticed',
    icon: '🔔',
    indicatorType: 'leading',
  },
  {
    key: 'rf',
    label: 'Reinforcement & Feedback',
    subtitle: 'Whether rewards/feedback sustain repetition',
    icon: '⭐',
    indicatorType: 'leading',
  },
  {
    key: 'bo',
    label: 'Behavioral Outcomes',
    subtitle: 'Automaticity, recovery, frequency, quality',
    icon: '🎯',
    indicatorType: 'lagging',
  },
]

// ─── Interpretation patterns (from HFA Interpretation Guide v4) ───────────────

export const HFA_PATTERNS = [
  {
    title: 'High Mechanics + Low Outcomes',
    interp: 'The environment supports adoption but the behavior is not translating into consistent practice. Accountability or expectation clarity is the gap — not the structural setup.',
    recs: [
      'Clarify the performance standard explicitly. Make the expected behavior observable and measurable.',
      'Increase manager check-in frequency with specific, behavior-referenced recognition.',
      'Do not redesign mechanics — the foundation is correct. Investigate accountability first.',
    ],
  },
  {
    title: 'Low Enablement (EE)',
    interp: 'Infrastructure is the primary barrier. Time, tools, or workflow integration are missing. No motivation intervention will overcome this — it is an operations problem.',
    recs: [
      'Conduct a workflow audit before any other intervention. Identify where the behavior sits and what must change.',
      'Protect time explicitly — build the behavior into meeting rhythms or daily routines.',
      'Run a tool access audit with real users, not IT sign-offs. Permissions and interface friction both count.',
    ],
  },
  {
    title: 'Low Planning + Low Cues (PR + CT)',
    interp: 'The behavior is entirely intention-based — no plan, no routine, no reliable environmental trigger. The most common adoption failure pattern and the most fixable.',
    recs: [
      'Run an if-then planning exercise: "I will perform [behavior] at [time] after [existing routine]." Specificity matters.',
      'Embed one reliable environmental cue — spatial or temporal proximity to the behavior.',
      'Review plans in manager one-on-ones during the first 30 days.',
    ],
  },
  {
    title: 'Low Reinforcement + Drifting Outcomes',
    interp: 'Early adoption is fading. The behavior is not being reinforced — employees complete the action but receive no meaningful signal it matters. Primary cause of behavioral drift after initial adoption.',
    recs: [
      'Build immediate feedback loops into the behavior — completion confirmations, progress indicators.',
      'Train managers to deliver specific, behavior-referenced recognition rather than general praise.',
      'Design milestone recognition moments at 30, 60, and 90 days to interrupt drift where it most often occurs.',
    ],
  },
  {
    title: 'Low Planning (PR)',
    interp: 'The behavior is not designed into routines. Employees treat it as something to do when they have time — which means it gets displaced by competing demands almost every day.',
    recs: [
      'Provide implementation intention templates at the point of training, not as optional extras.',
      'Anchor the new behavior to an existing stable routine through habit stacking. Specific anchors beat general ones.',
      'Reduce startup friction — pre-load what employees need before the behavior begins.',
    ],
  },
  {
    title: 'High BO3 (frequency) but Low BO4 (quality)',
    interp: 'The behavior is happening but not to standard. Most often reflects unclear definitions or inadequate calibration, not insufficient effort.',
    recs: [
      'Clarify the quality standard explicitly and provide calibrated examples of what good looks like.',
      'Use coaching and job aids rather than more training volume — the issue is clarity, not capability.',
      'Conduct a brief quality calibration session with supervisors and frontline staff together.',
    ],
  },
]
